// src/utils/createUserWithStudents.js
import {
  getFirestore,
  doc,
  setDoc,
  serverTimestamp,
  runTransaction,
} from "firebase/firestore";
import { getApp, getApps, initializeApp } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import { publishToDirectory } from "./educatorCode";

// ---------- Helper: Slugify ----------
function slugify(s = "") {
  return String(s).toLowerCase().replace(/[^a-z0-9]/g, "");
}

// ---------- Helper: Educator-code password for students (Mode A) ----------
function makeEduCodePassword(educatorLastName = "", educatorCode = "") {
  const last = slugify(educatorLastName);
  const code = String(educatorCode || "");
  let pw = `${last}${code}`;
  while (pw.length < 6) pw += "1"; // ensure Firebase min length
  return pw;
}

// ---------- Helper: Simple word+digits fallback password ----------
const PW_WORDS_4 = ["blue", "tree", "star", "frog", "bear", "lion", "book", "moon", "ship", "fish"];

function generateRandomWordPassword() {
  const word = PW_WORDS_4[Math.floor(Math.random() * PW_WORDS_4.length)];
  const num = String(10 + Math.floor(Math.random() * 90)); // 10–99
  const firstLetter = word.charAt(0).toUpperCase();
  const rest = word.slice(1);
  return `${firstLetter}${rest}${num}`; // e.g., Blue27
}

// ---------- Helper: Educator password from school name (your new rule) ----------
function makeEducatorPasswordFromSchoolName(schoolName = "") {
  const trimmed = String(schoolName || "").trim();
  if (!trimmed) return generateRandomWordPassword();

  const parts = trimmed.split(/\s+/);
  const first = parts[0] || "";

  if (first.length >= 6) {
    return first.charAt(0).toUpperCase() + first.slice(1);
  }

  // first word < 6 characters → add second word
  if (parts.length >= 2) {
    const second = parts[1] || "";
    return (first + second).charAt(0).toUpperCase() + (first + second).slice(1);
  }

  // only one short word → add "School"
  const merged = first + "School";
  return merged.charAt(0).toUpperCase() + merged.slice(1);
}


// ---------- Helpers: Counters ----------
async function allocateEducatorCode(db, step = 5, startAt = 471020) {
  const ref = doc(db, "counters", "educatorCode");
  const code = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      // First time use
      tx.set(ref, { next: startAt + step });
      return startAt;
    }
    const data = snap.data() || {};
    const current = typeof data.next === "number" ? data.next : startAt + step;
    tx.update(ref, { next: current + step });
    return current;
  });
  return code;
}

async function allocateStudentSeqBlock(db, educatorUid, count) {
  const ref = doc(db, "counters", `students_${educatorUid}`);
  const start = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      tx.set(ref, { next: 1 + count });
      return 1;
    }
    const data = snap.data() || {};
    const next = typeof data.next === "number" ? data.next : 1;
    tx.update(ref, { next: next + count });
    return next;
  });
  return { start, count };
}

function isEducatorRole(role = "") {
  const r = String(role || "").toLowerCase();
  return r === "educator" || r.endsWith("educator") || r.endsWith("-educator");
}

// ---------- Helper: Student email ----------
function makeStudentEmail(studentFirst, studentLastInitial, educatorCode, seq) {
  const li = (studentLastInitial || "").trim();
  const base = `${slugify(studentFirst)}${li ? slugify(li) : ""}`.slice(0, 12) || "student";
  const num = String(seq).padStart(2, "0");
  return `${base}${num}-${educatorCode}@sbl.school`;
}

// ---------- Helper: Secondary Auth (so admin's session isn't swapped) ----------
function getSecondaryAuth() {
  const primary = getApp();
  const name = "Secondary-App";
  const existing = getApps().find((a) => a.name === name);
  const secondary = existing || initializeApp(primary.options, name);
  const secondaryAuth = getAuth(secondary);
  return { secondary, secondaryAuth };
}

// ---------- MAIN EXPORT ----------
/**
 * Create an educator + optional students in one go.
 *
 * @param {Object} params
 * @param {import("firebase/firestore").Firestore} params.db
 * @param {string|null} params.adminUid - UID of the admin performing the action
 * @param {Object} params.educator - educator data (sirName, firstName, lastName, email, etc.)
 * @param {Array<{firstName: string, lastInitial?: string}>} params.students
 *
 * @returns {Promise<{educator: {...}, students: Array<...>}>}
 */
export async function createEducatorWithStudents({ db, adminUid, educator, students = [] }) {
  const auth = getAuth();
  const actingUid = adminUid || auth.currentUser?.uid || null;
  if (!actingUid) {
    throw new Error("No admin user is signed in. Cannot perform bulk user creation.");
  }

  const {
    sirName = "",
    firstName = "",
    lastName = "",
    email = "",
    password = "", // optional override, usually blank for bulk
    schoolName = "",
    schoolDistrict = "",
    state = "",
    grade = "",
    view = "Educator",
    role = "",
    notes = "",
    educatorCode: providedEducatorCode = "",
  } = educator || {};

  if (!firstName || !email || !state || !grade || !view || !role) {
    throw new Error(`Missing required educator fields for ${firstName || "(no name)"}.`);
  }

  const dbInstance = db || getFirestore();

  // Decide educator password:
  //   1) If Excel provides one, use it
  //   2) Else derive from School Name
  const educatorPassword =
    (password && password.trim().length >= 6 ? password.trim() : makeEducatorPasswordFromSchoolName(schoolName));

  const { secondary, secondaryAuth } = getSecondaryAuth();

  try {
    // ---------- 1. Educator Auth ----------
    const userCred = await createUserWithEmailAndPassword(secondaryAuth, email, educatorPassword);
    const educatorUid = userCred.user.uid;

    // ---------- 2. Educator Code (for educator roles only) ----------
    let educatorCode = providedEducatorCode;
    if (isEducatorRole(role) && !educatorCode) {
      educatorCode = String(await allocateEducatorCode(dbInstance, 5, 471020));
    }

    // ---------- 3. Write educator doc ----------
    const educatorDoc = {
      sirName,
      firstName,
      lastName,
      email,
      schoolName,
      schoolDistrict,
      state,
      grade,
      view,
      role,
      notes,
      educatorCode,
      joinDate: new Date().toISOString(),
      createdAt: serverTimestamp(),
      createdBy: actingUid,
      avatarUrl:
        "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/avatars%2Favatar24.png?alt=media&token=4ff5f59e-fd2f-4950-b984-f7f849f7b291",
    };

    await setDoc(doc(dbInstance, "users", educatorUid), educatorDoc);
    // so students can find this educator's code at sign-up straight away
    if (educatorCode) await publishToDirectory(educatorUid, educatorCode, educatorDoc);

    // Optional: store educator temp password too
    try {
      await setDoc(
        doc(dbInstance, "usersPrivate", educatorUid),
        {
          tempPassword: educatorPassword,
          createdAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (e) {
      console.warn("Failed to store educator tempPassword in usersPrivate:", e);
    }

    // ---------- 4. Students (if any and this is an educator role) ----------
    const createdStudents = [];

    if (isEducatorRole(role) && Array.isArray(students) && students.length > 0) {
      const validStudents = students.filter((s) => (s.firstName || "").trim());
      if (validStudents.length > 0) {
        const { start } = await allocateStudentSeqBlock(dbInstance, educatorUid, validStudents.length);

        let i = 0;
        for (const raw of validStudents) {
          const studentFirst = String(raw.firstName || "").trim();
          const studentLastInitial = String(raw.lastInitial || "").trim().slice(0, 1);

          if (!studentFirst) continue;

          const studentNumber = start + i;
          i++;

          const studentEmail = makeStudentEmail(studentFirst, studentLastInitial, educatorCode, studentNumber);
          const studentPassword = makeEduCodePassword(lastName || "teacher", educatorCode);

          const stCred = await createUserWithEmailAndPassword(secondaryAuth, studentEmail, studentPassword);
          const studentUid = stCred.user.uid;

          const studentDoc = {
            sirName: "N/A",
            firstName: studentFirst,
            lastName: studentLastInitial || "",
            email: studentEmail,
            state,
            grade,
            view: "Student",
            role: String(role || "").toLowerCase().replace(/-educator$/, "-student"),
            schoolName,
            schoolDistrict,
            notes: `Auto-created under educator ${firstName} ${lastName || ""}`,
            educatorCode,
            educatorUid,
            studentNumber,
            createdAt: serverTimestamp(),
            createdBy: actingUid,
            avatarUrl:
              "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/avatars%2Favatar24.png?alt=media&token=4ff5f59e-fd2f-4950-b984-f7f849f7b291",
          };

          await setDoc(doc(dbInstance, "users", studentUid), studentDoc);

          try {
            await setDoc(doc(dbInstance, "usersPrivate", studentUid), {
              tempPassword: studentPassword,
              educatorUid,
              createdAt: serverTimestamp(),
            });
          } catch (e) {
            console.warn(`usersPrivate/${studentUid} write failed`, e);
          }

          createdStudents.push({
            uid: studentUid,
            name: `${studentFirst}${studentLastInitial ? ` ${studentLastInitial}.` : ""}`,
            email: studentEmail,
            password: studentPassword,
            studentNumber,
          });
        }
      }
    }

    return {
      educator: {
        uid: educatorUid,
        // NEW FIELDS → return REAL names
        sirName,
        firstName,
        lastName,
    
        // optional combined name
        name: `${sirName} ${lastName}`.trim(),
    
        email,
        password: educatorPassword,
        schoolName,
        educatorCode,
      },
      students: createdStudents,
    };
    
  } finally {
    try {
      // clean up secondary app if desired
      // import { deleteApp } from "firebase/app" if you want to fully tear down
    } catch {
      // ignore
    }
  }
}
